import { Skeleton } from '@mui/material';
import type { JSX } from 'react';
import useLunarRunStats from '../hooks/useLunarRunStats';
import formatWaitingTime from '../utils/formatWaitingTime';

type ValueTone = 'neutral' | 'danger';

const VALUE_TONE_CLASSES: Record<ValueTone, string> = {
  neutral: 'vtwo:text-typography-primary',
  danger: 'vtwo:text-[#bd3437]',
};

export default function LunarRunStats(): JSX.Element {
  const { data, isError } = useLunarRunStats();

  if (isError) {
    return (
      <p className="vtwo:text-sm vtwo:text-typography-danger">
        Couldn&apos;t load the review stats
      </p>
    );
  }

  const notHelpfulCount = data?.notHelpfulCount;

  return (
    <dl className="vtwo:grid vtwo:grid-cols-1 vtwo:gap-3 vtwo:sm:grid-cols-3">
      <StatCard
        label="Oldest pending"
        value={data && formatWaitingTime(data.oldestPendingTimestamp)}
        caption="Clear the queue daily"
      />
      <StatCard
        label="Not helpful"
        value={notHelpfulCount}
        caption="Runs the patient voted down"
        tone={notHelpfulCount ? 'danger' : 'neutral'}
      />
      <StatCard
        label="Terminal tool"
        value={data?.terminalCount}
        caption="Runs that ended with the fallback card"
      />
    </dl>
  );
}

interface StatCardProps {
  label: string;
  /** Undefined while the stats load. */
  value: string | number | undefined;
  caption: string;
  tone?: ValueTone;
}

function StatCard({ label, value, caption, tone = 'neutral' }: StatCardProps): JSX.Element {
  return (
    <div className="vtwo:rounded-lg vtwo:border vtwo:border-border vtwo:bg-white vtwo:px-4 vtwo:py-3">
      <dt className="vtwo:text-[11px] vtwo:font-semibold vtwo:uppercase vtwo:leading-4 vtwo:tracking-[0.06em] vtwo:text-typography-secondary">
        {label}
      </dt>
      <dd
        className={`vtwo:text-[22px] vtwo:font-semibold vtwo:leading-7 ${VALUE_TONE_CLASSES[tone]}`}
      >
        {value === undefined ? <Skeleton width={64} /> : value}
      </dd>
      <dd className="vtwo:mt-1 vtwo:text-xs vtwo:leading-4 vtwo:text-typography-secondary">
        {caption}
      </dd>
    </div>
  );
}
